import React, { useState } from "react"
import { Link } from "react-router-dom"

import { Notification } from "@/components"

import RDStationCRMService from "@/services/RDStationCRM"

import { RDStationCRMDeal, RDStationCRMPipeline, RDStationCRMPluginSettingsData } from "@/protocols/RdStationPluginProtocol"
import { RDStationCRMDealStatus } from "@/protocols/chatBot"

import { ValidationContent } from "@/hooks/useActiveCampaignApi"

export type RDStationCRMPluginStatus = "success" | "plugin-disabled" | "plugin-check-error"

export type RDStationCRMWarningsType =
	"plugin-data-not-found" |
	"invalid-pipeline" |
	"invalid-stage" |
	"pipelines-not-found" |
	RDStationCRMPluginStatus

type DealStatusOption = {
	name: string
	slug: RDStationCRMDealStatus
}

const pluginWarnings: Record<RDStationCRMWarningsType, ValidationContent> = {
	"plugin-disabled": {
		type: "warning",
		text: (
			<>
				Para que esse bloco funcione corretamente, ative o plugin do <Link to="/admin/plugins/rd-station-crm" target='_blank'>RD Station CRM</Link>.
			</>
		)
	},
	"plugin-data-not-found": {
		type: "warning",
		text: (
			<>
				As informações do plugin do <Link to="/admin/plugins/rd-station-crm" target='_blank'>RD Station CRM</Link> não estão corretas, certifique-se de que esteja tudo certo para que o bloco funcione corretamente.
			</>
		)
	},
	"plugin-check-error": {
		type: "warning",
		text: (
			<>
				Não foi possível conectar na API do <Link to="/admin/plugins/rd-station-crm" target='_blank'>RD Station CRM</Link>.
			</>
		)
	},
	"invalid-pipeline": {
		type: "warning",
		text: <>O Funil de Vendas abaixo não existe ou foi alterado. Verifique na sua conta do RD Station CRM a validade deste Funil de Vendas.</>
	},
	"invalid-stage": {
		type: "warning",
		text: <>A Etapa abaixo não existe ou foi alterada. Verifique na sua conta do RD Station CRM a validade desta Etapa.</>
	},
	"pipelines-not-found": {
		type: "warning",
		text: <>Não foram encontrados Funis de Vendas na conta cadastrada.</>
	},
	success: {
		type: "success",
		text: (<></>)
	}
}

const useRDStationCRMApi = () => {
	const [status, setStatus] = useState<RDStationCRMWarningsType>("success")
	const [isValidPlugin, setIsValidPlugin] = useState<boolean>(false)

	const DEAL_STATUS_OPTIONS: DealStatusOption[] = [
		{ name: "Em andamento", slug: "ongoing" },
		{ name: "Ganho", slug: "won" },
		{ name: "Perdido", slug: "lost" },
		{ name: "Pausado", slug: "paused" }
	]

	const getPluginSettings = async (): Promise<RDStationCRMPluginSettingsData | undefined> => {
		try {
			const pluginSettings = await RDStationCRMService.checkAuthCredentials()

			return pluginSettings
		} catch (error) {
			Notification.error({ message: "Não foi possível carregar os dados do plugin." })
		}
	}

	const validatePluginData = (data: RDStationCRMPluginSettingsData): boolean => {
		if (data.status !== "enabled") {
			setStatus("plugin-disabled")
			return false
		}

		if (!data.settings?.access_token) {
			setStatus("plugin-data-not-found")
			return false
		}

		return true
	}

	const validatePlugin = async (): Promise<boolean> => {
		const pluginSettings = await getPluginSettings()

		if (!pluginSettings) {
			setStatus("plugin-disabled")
			setIsValidPlugin(false)
			return false
		}

		const isValidPluginData = validatePluginData(pluginSettings)

		if (!isValidPluginData) {
			setIsValidPlugin(false)
			return false
		}

		setStatus("success")
		setIsValidPlugin(true)

		return true
	}

	const pipelinesAlreadyExistInRDStationCRM = (pipelines: RDStationCRMPipeline[]): boolean => {
		if (pipelines.length === 0) {
			return false
		}

		return true
	}

	const isValidPipeline = (pipelines: RDStationCRMPipeline[], deal: RDStationCRMDeal): RDStationCRMPipeline | null => {
		const pipelineAlreadyExists = pipelines?.find(group => {
			return group.title === deal?.pipeline?.title
		})

		if (!pipelineAlreadyExists) {
			return null
		}

		return pipelineAlreadyExists
	}

	const isValidStage = (pipeline: RDStationCRMPipeline, deal: RDStationCRMDeal): boolean => {
		const stageAlreadyExists = pipeline.stages?.find(stage => stage.title === deal.stage.title)

		if (!stageAlreadyExists) {
			return false
		}

		return true
	}

	const validateDealRegistration = (pipelines: RDStationCRMPipeline[], deal: RDStationCRMDeal): boolean => {
		const pipelineAlreadyExists = pipelinesAlreadyExistInRDStationCRM(pipelines)

		if (!pipelineAlreadyExists) {
			setStatus("pipelines-not-found")
			return false
		}

		if (deal) {
			const pipeline = isValidPipeline(pipelines, deal)

			if (!pipeline) {
				setStatus("invalid-pipeline")
				return false
			}

			const stageIsValid = isValidStage(pipeline, deal)

			if (!stageIsValid) {
				setStatus("invalid-stage")
				return false
			}
		}

		setStatus("success")

		return true
	}

	const getValidationContent = () => {
		return pluginWarnings[status]
	}

	const getPipelineAndStageTitle = (deal: Partial<RDStationCRMDeal>) => {
		const pipeline = {
			title: ""
		}

		const stage = {
			title: ""
		}

		const pipelineAlreadyExists = status !== "invalid-pipeline" && status !== "pipelines-not-found" && !!deal?.pipeline
		const dealStageAlreadyExists = status !== "invalid-stage" && !!deal?.stage

		if (pipelineAlreadyExists) {
			pipeline.title = deal?.pipeline?.title as string
		}

		if (dealStageAlreadyExists) {
			stage.title = deal?.stage?.title as string
		}

		return {
			pipelineTitle: pipeline.title,
			stageTitle: stage.title
		}
	}

	return {
		validatePlugin,
		getValidationContent,
		validateDealRegistration,
		status,
		getPipelineAndStageTitle,
		isValidPlugin,
		DEAL_STATUS_OPTIONS
	}
}

export default useRDStationCRMApi
